// @flow
import { Error, VerifiedOutlined } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useContext, useMemo } from 'react';
import { useTranslation } from "react-i18next";
import { EmailValid } from "../../../hooks/useValidateEmail";
import { VISITOR_PROFILE } from "../../../locales/components/namespaces";
import { ErrorMessage } from "formik";
import TextField from "@mui/material/TextField";
import { PreRegistrationContext } from "../../form/form";
import { useTextFieldChange } from "../../../hooks/preRegistration/useTextFieldChange";
import TaxisSearch from "./taxisSearch";
import FieldText from "./fieldText";

type Props = {};

export const textfieldProps = (props, t) => {
	const { field, touched, errors } = props
	return {
		label: t(field.name),
		name: "fieldValues." + field.id,
		required: field.required,
		autoComplete: "off",
		size: "small",
		fullWidth: true,
		variant: "outlined",
		sx: {
			'& .MuiOutlinedInput-input': {
				bgcolor: "background.paper"
			}
		},
		error: touched["fieldValues"] &&
			touched["fieldValues"][field.id] &&
			Boolean(errors["fieldValues"] && errors["fieldValues"][field.id]),
		helperText: <ErrorMessage name={"fieldValues." + field.id}/>
	}
}

const Text = (props: Props) => {
	const { formik, values, field, value: propValue, handleBlur, handleChange, touched, errors, disabled } = props
	const { t } = useTranslation(VISITOR_PROFILE)
	const {
		companyNameFieldRef,
		nameFieldRef,
		surnameFieldRef,
		vatFieldRef,
		emailFieldRef,
		phoneFieldRef,
		companyAddressFieldRef,
		cityFieldRef,
		companyWebsiteFieldRef,
		instagramFieldRef,
		facebookFieldRef,
		positionFieldRef,
		personNumberFieldRef,
		postalCodeFieldRef,
		filledByTaxisSearch,
		setFilledByTaxisSearch,
		emailIsValid,
		validateEmail
	} = useContext(PreRegistrationContext)
	const { handleChange: onChange, value } = useTextFieldChange({
		handleChange,
		field,
		value: propValue
	})
	
	let fieldRef = null
	switch (field.id) {
		case 'companyName':
			fieldRef = companyNameFieldRef
			break
		case 'name':
			fieldRef = nameFieldRef
			break
		case 'surname':
			fieldRef = surnameFieldRef
			break
		case 'vatNumber':
			fieldRef = vatFieldRef
			break
		case 'email':
			fieldRef = emailFieldRef
			break
		case 'phone':
			fieldRef = phoneFieldRef
			break
		case 'companyAddress':
			fieldRef = companyAddressFieldRef
			break
		case 'city':
			fieldRef = cityFieldRef
			break
		case 'companyWebsite':
			fieldRef = companyWebsiteFieldRef
			break
		case 'instagram':
			fieldRef = instagramFieldRef
			break
		case 'facebook':
			fieldRef = facebookFieldRef
			break
		case 'position':
			fieldRef = positionFieldRef
			break
		case 'personCount':
			fieldRef = personNumberFieldRef
			break
		default:
			break
	}
	
	const textFieldProps = textfieldProps(props, t)
	
	const returnField = useMemo(() => {
		if (field.id === "postalCode") {
			return (
				<TextField
					{...textFieldProps}
					type={field.inputType}
					onChange={onChange}
					InputLabelProps={{ shrink: true }}
					defaultValue={value}
					autoComplete='off'
					inputRef={postalCodeFieldRef}
					inputProps={{ maxLength: field?.maxLength }}
					disabled={disabled}
				/>
			)
		}
		if (field.id === 'vatNumber' && field.enableTaxisSearchPreregistration) {
			return (
				<TaxisSearch
					field={field}
					value={value}
					handleBlur={handleBlur}
					formik={formik}
					fieldRef={fieldRef}
					values={values}
					handleChange={handleChange}
					touched={touched}
					errors={errors}
					disabled={disabled}
					filledByTaxisSearch={filledByTaxisSearch}
					setFilledByTaxisSearch={setFilledByTaxisSearch}
				/>
			)
		}
		if (field.id === 'email') {
			return (
				<>
					<Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
						<FieldText
							textfieldProps={{
								...textFieldProps,
								sx: {
									...textFieldProps.sx,
									'& .MuiOutlinedInput-root': {
										'& fieldset': {
											borderColor: emailIsValid === EmailValid.Valid ? '#4caf50' : emailIsValid === EmailValid.Error ? '#ed6c02' : 'auto',
										},
										'&:hover fieldset': {
											borderColor: emailIsValid === EmailValid.Valid ? '#4caf50' : emailIsValid === EmailValid.Error ? '#ed6c02' : 'auto',
										},
										'&.Mui-focused fieldset': {
											borderColor: emailIsValid === EmailValid.Valid ? '#4caf50' : emailIsValid === EmailValid.Error ? '#ed6c02' : 'auto',
										},
									}
								}
							}}
							field={field}
							value={value}
							handleBlur={(evt) => {
								handleBlur(evt)
								validateEmail(evt.target.value)
							}}
							formik={formik}
							fieldRef={fieldRef}
							values={values}
							handleChange={handleChange}
							touched={touched}
							errors={errors}
							disabled={disabled}
						/>
						<Box>
							{emailIsValid === EmailValid.Valid ? (
								<VerifiedOutlined color={'success'} sx={{ marginLeft: 0.5 }}/>
							) : (emailIsValid === EmailValid.Error) ? (
								<Error color={'warning'} sx={{ marginRight: 0.5 }}/>
							) : null}
						</Box>
					</Box>
					{emailIsValid === EmailValid.Error && (
						<Typography variant="body2" sx={{ marginLeft: 1, color: '#ed6c02' }}>
							{t('Email may be incorrect.')}
						</Typography>
					)}
				</>
			)
		}
		return (
			<FieldText
				textfieldProps={textFieldProps}
				field={field}
				value={value}
				handleBlur={handleBlur}
				formik={formik}
				fieldRef={fieldRef}
				values={values}
				handleChange={handleChange}
				touched={touched}
				errors={errors}
				disabled={disabled}
			/>
		)
	}, [disabled, emailIsValid, errors, field, fieldRef, filledByTaxisSearch, formik, handleBlur, handleChange, onChange, postalCodeFieldRef, setFilledByTaxisSearch, t, textFieldProps, touched, validateEmail, value, values])
	
	return returnField
};

export default Text